<template>
    <div class="container">
        <div class="title-container">
            <span>
                {{ $t('intervention.title') }} {{ this.$route.params.id }}
                <ReglePastille cle="interventionNonPreventive.definition" />
                <ReglePastille cle="suiviInterventionsNonPreventivesDetail.rapportDisponible" />
            </span>
        </div>
        <BorderedIconButton title="RETOUR" :imgUrl="imgUrl" className="back-btn" :onClick="this.$router.back" />
        <StatusTag :status="this.status" />
        <DataList v-if="this.interventionData && this.isLoaded"
                  :data="this.interventionData"
                  :stopPropaOnPdf="true"
                  @select="goToPage" @downloadPdf="downloadPdf"/>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DataList from '@/components/ui-components/DataList.vue';
import StatusTag, { statusName } from '@/components/ui-components/StatusTag.vue';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import ErrorManager from '@/common/utils/ErrorManager';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import SuiviInterventionService from '@/modules/SuiviInterventions/services/SuiviInterventionService';
import DocumentReglementaireService from '@/modules/DocumentReglementaire/services/DocumentReglementaireService';
import router from '@/router';
import { InterventionDetailDto } from '@/models/InterventionDetailDto';
import { equipementsToString } from '@/models/EquipementItems';
import DownloadPdf from '@/common/utils/DownloadPdf';
import ReglePastille from '@/components/regles/ReglePastille.vue';

type DataType = {
    interventionData: Array<any>;
    status: string | null;
    isLoaded: boolean;
    rapportTitre?: string;
    equipementIds?: number[];
};

interface DemandeIntervention {
    id: number;
    objet: string;
    type: string;
    emmitOn: string;
    demandeur: string;
}

export default defineComponent({
    name: 'InterventionDetailPage',
    components: { DataList, StatusTag, BorderedIconButton, ReglePastille },
    data(): DataType {
        return {
            interventionData: [],
            isLoaded: false,
            status: null,
        };
    },

    beforeMount() {
        const interventionId = +this.$route.params.id;

        SuiviInterventionService.getIntervention(interventionId)
            .then((response: { data: InterventionDetailDto }) => {
                const inter = response.data;
                this.status = statusName(inter.etat);

                let observations = '';
                inter.observations?.forEach(observation => {
                    if(!observation.privee){
                        observations += observation.observation;
                        observations += '\n \n';
                    }
                });

                this.interventionData = [
                    { label: this.$t('dataList.chantierLibelle'), value: inter.site },
                    { label: this.$t('label.numIntervention'), value: inter.id },
                    { label: this.$t('dataList.objet'), value: inter.objet },
                    {
                        label: this.$t('dataList.dateCreation'),
                        value: inter.dateCreation ? new Date(inter.dateCreation).toLocaleDateString() : '',
                    },
                    {
                        label: this.$t('dataList.dateCloseOn'),
                        value: inter.dateCloture ? new Date(inter.dateCloture).toLocaleDateString() : '',
                    },
                    {
                        label: this.$t('dataList.predicatedDate'),
                        value: inter.dateDebutPrevue ? new Date(inter.dateDebutPrevue).toLocaleDateString() : '',
                    },
                    {
                        label: this.$t('dataList.dateRealise'),
                        value: inter.dateFinReelle ? new Date(inter.dateFinReelle).toLocaleDateString() : '',
                    },
                    {
                        label: this.$t('dataList.equipment', {count: inter.equipements?.length}),
                        value: inter.equipements ? equipementsToString(inter.equipements) : ''
                    },
                    { label: this.$t('dataList.observations'), value: observations },
                    {
                        label: this.$t('dataList.alarmante'),
                        value: inter.observations?.[0]?.alarme ? this.$t('yes') : this.$t('no'),
                    },
                ];

                if (inter.isRapportPresent) {
                    this.interventionData.push({
                        label: this.$t('dataList.interventionReport'),
                        value: { pdfExist: true },
                    });
                } else {
                    this.interventionData.push({
                        label: this.$t('dataList.interventionReport'),
                        value: this.$t('dataList.interventionReportMissing'),
                    });
                }

                this.interventionData.push({
                    label: this.$t('dataList.adresse'),
                    value: inter.adresse,
                });

                this.rapportTitre = inter.rapportTitre;
                this.isLoaded = true;

                return SuiviInterventionService.getRapportInfo(interventionId);
            })
            .then(() => {
                return SuiviInterventionService.getListDemandeInterventionByNonPreventiveInterventionId(interventionId);
            })
            .then((res: { data: DemandeIntervention[] }) => {
                const tableHeader = [
                    { label: this.$t('dataList.demandeId') },
                    { label: this.$t('label.objet') },
                    { label: this.$t('label.type') },
                    { label: this.$t('label.emitOn') },
                    { label: this.$t('label.emmiter') },
                ];

                const tableData = res.data.map(({ id, objet, type, emmitOn, demandeur }: DemandeIntervention) => ({
                    id: { pageName: 'interventionRequestDetailView', value: id, label: id },
                    objet,
                    type,
                    emmitOn,
                    demandeur,
                }));

                this.interventionData.push({
                    label: this.$t('dataList.linkRequest'),
                    value: { tableHeader, tableData, noDataText: this.$t('intervention.noDataText') },
                });

                return DocumentReglementaireService.getDocumentReglementaire({
                    interventionId: interventionId
                });
            })
            .then((res: { data: any[] }) => {
                if (res && res.data) {
                    const tableHeader = [
                        { label: this.$t('dataList.documentType') },
                        { label: this.$t('dataList.equipment', {count:2}) },
                        { label: 'PDF' },
                    ];

                    interface DocumentReglementaireItem {
                        typeDocumentReglementaireDTO?: { libelle?: string };
                        libelleEquipement?: string;
                        phaseLibelle?: string;
                        uniqueDocumentId: string;
                        nomDocument: string;
                        extension: string;
                    }

                    const tableData = res.data.map((doc: DocumentReglementaireItem) => ({
                        type: doc.typeDocumentReglementaireDTO?.libelle || '',
                        equipment: doc.phaseLibelle && doc.phaseLibelle.trim().toLowerCase() !== 'maintenance' ? `${doc.libelleEquipement} - ${doc.phaseLibelle}` : (doc.libelleEquipement || ''),
                        pdfExist: true,
                        invisibleUniqueDocumentId: doc.uniqueDocumentId,
                        invisibleNomDocument: doc.nomDocument,
                        invisibleExtension: doc.extension
                    }));

                    this.interventionData.push({
                        label: this.$t('dataList.regulatoryDocuments'),
                        value: {
                            tableHeader,
                            tableData,
                            noDataText: this.$t('regulatoryDocuments.noDataText'),
                            onPdfClick: this.downloadDocumentReglementairePdf
                        },
                    });
                }
            })
            .catch(async (err: any) => {
                try {
                    const message = await ErrorManager.processError(err.response);
                    this.handleError(message);
                } catch (processError) {
                    this.handleError('Erreur lors du traitement de l\'erreur');
                    console.error('Error processing error:', processError);
                }
            });
    },

    computed: {
        imgUrl() {
            return require('@/assets/img/chevron-left-white.svg');
        },
    },
    methods: {
        handleError(message: string): void {
            this.$store.commit(`errorToaster/${ERROR.SET_ACTIVE}`, true);
            this.$store.commit(`errorToaster/${ERROR.SET_MESSAGE}`, message);
        },
        goToPage(item: { id: { pageName: string; value: number; }; }){
            router.push({ name: item.id.pageName, params: { id: item.id.value } });
        },
        async downloadPdf(item?: any) : Promise<void> {
            if (item && item.invisibleUniqueDocumentId) {
                // If item has invisibleUniqueDocumentId, it's a regulatory document
                await this.downloadDocumentReglementairePdf(item);
            } else {
                // Otherwise, it's an intervention report
                DownloadPdf.getInterventionPdf(+this.$route.params.id, 'Rapport_intervention-');
            }
        },
        async downloadDocumentReglementairePdf(item: any) : Promise<void> {
            try {
                // V�rifier si les propri�t�s n�cessaires sont d�finies
                if (!item.invisibleNomDocument || !item.invisibleExtension) {
                    console.warn('Propri�t�s manquantes pour le t�l�chargement du document:', item);
                    // R�cup�rer les informations manquantes si possible
                    // Ou utiliser des valeurs par d�faut
                    item.invisibleNomDocument = item.invisibleNomDocument || `Document_${item.invisibleUniqueDocumentId}`;
                    item.invisibleExtension = item.invisibleExtension || 'pdf';
                }

                const response = await DocumentReglementaireService.getDocumentReglementairePDF({
                    uniqueDocumentIds: [item.invisibleUniqueDocumentId],
                    nomDocument: item.invisibleNomDocument,
                    extension: item.invisibleExtension
                });

                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${item.invisibleNomDocument}.${item.invisibleExtension}`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (error) {
                console.error('Error downloading regulatory document:', error);
                this.$store.commit(`errorToaster/${ERROR.SET_ACTIVE}`, true);
                this.$store.commit(`errorToaster/${ERROR.SET_MESSAGE}`, 'Erreur lors du t�l�chargement du document r�glementaire');
            }
        }
    }
});
</script>

<style scoped>
.container {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 24px;
    white-space: pre-line;
}
.title-container {
    color: var(--color-principal);
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-600);
    margin-bottom: 15px;
}
</style>
